<script setup lang="ts">
    import useBuefy from '@/shared/composables/useBuefy';
    import useShow from '@/shared/composables/useShow';
    import { createShippingPackage, ShippingPackage } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper';
    import { useShippingStore } from '@/stores/shippingStore';

    const store = useShippingStore();
    const buefy = useBuefy();
    const { showError } = useShow();

    // Prefixing with underscore to satisfy current linter setup for .vue files
    type CopyContinuation = (_packageToCopy: ShippingPackage, _newName: string) => Promise<void>;

    async function packageCopyName(
        shippingPackage: ShippingPackage,
        next: CopyContinuation): Promise<void> {
            const templateName: string = shippingPackage.name || shippingPackage.packageId;
            await buefy.dialog.prompt({
                message: 'Give the new package a name. Consider adding a version number or todays date to distinguish the package from the original package.',
                inputAttrs: {
                    maxlength: 256,
                    value: templateName
                },
                trapFocus: true,
                onConfirm: value => {
                    next(shippingPackage, value);
                }
            });
        }

    async function createCopyWithName(shippingPackage: ShippingPackage, name: string) {
        const loading = buefy.loading.open({});
        try {
            const result = await createShippingPackage(
                http,
                shippingPackage.packageType,
                name,
                shippingPackage.description,
                shippingPackage.remarks || '',
                JSON.parse(shippingPackage.extractionOptions));

            await store.clearSelectedPackage();

            const newPackageId = result.data.packageId;
            const newPackage = store.packages.find(x => x.packageId === newPackageId);
            if (newPackage) {
                await store.setSelectedPackage(newPackage);
            }
        } catch (err) {
            showError(err);
        } finally {
            loading.close();
        }
    }

    async function onReExport(): Promise<void> {
        if (!store.selectedPackage)
            return;

        await packageCopyName(store.selectedPackage, createCopyWithName);
    }
</script>

<template>
    <template v-if="store.isDownloadAvailable">
        <div class="block">
            <div class="shipping-section-header block">
                Export again
            </div>
            <div class="is-size-5">
                You may also create a new package using the exact same settings as this package.
                The new package will contain the current state from Common Library.
                This is usefull if you need to do the same transfer of data at a later time.
            </div>
        </div>
        <div
            class="is-flex is-justify-content-center pt-5 pb-5">
            <b-button
                type="is-primary"
                size="is-large"
                outlined
                @click="onReExport">
                Re-export with same settings
            </b-button>
        </div>
    </template>
</template>
